import { datadogRum } from "@datadog/browser-rum";
import classNames from "classnames/bind";
import { EmbeddedVideoDetails } from "features/Assets/components/EmbeddedVideo/EmbeddedVideoDetails";
import { EmbeddedVideoPlayer } from "features/Assets/components/EmbeddedVideo/EmbeddedVideoPlayer";
import { EntityType } from "features/common/types";
import { type FlowEmbeddedVideoAsset } from "features/Flows/types";
import { useCallback, type MutableRefObject } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { completeEmbeddedVideoAsset, skip, startTransition } from "../../redux/flow/flowSlice";
import { markEmbeddedVideoAsStarted } from "../../services/flowsService";
import styles from "./FlowEmbeddedVideo.module.scss";

const cx = classNames.bind(styles);

export interface Props {
  order: number;
  embeddedVideo: FlowEmbeddedVideoAsset;
  hidden: boolean;
  resetAssetRef?: MutableRefObject<(() => void) | undefined>;
}

export const FlowEmbeddedVideo: React.FC<Props> = ({ order, embeddedVideo, hidden }) => {
  const { title, description, id, videoUrl, videoSourceId } = embeddedVideo;

  const { flowId } = useParams();
  const dispatch = useDispatch();

  const onVideoStart = useCallback(() => {
    datadogRum.addAction("On flow embedded video starting", {
      flowId: flowId,
      embeddedVideoId: id,
    });

    (async () => {
      if (flowId && id) await markEmbeddedVideoAsStarted(+flowId, id);
      datadogRum.addAction("On flow video started", {
        flowId: flowId,
        videoId: id,
      });
    })();
  }, [flowId, id]);

  const onVideoEnd = useCallback(() => {
    (async () => {
      if (flowId && id) {
        dispatch(
          startTransition({
            assetOrder: order,
          }),
        );
        dispatch(
          completeEmbeddedVideoAsset({
            flowId: +flowId,
            assetId: id,
            assetOrder: order,
          }),
        );
      }
    })();
  }, [dispatch, flowId, id, order]);

  const onSkip = useCallback(() => {
    dispatch(startTransition({ assetOrder: order }));
    dispatch(skip({ assetId: id, assetType: EntityType.EmbeddedVideo }));
  }, [dispatch, id, order]);

  return (
    <div className={cx(styles.root, { [styles.hidden]: hidden })}>
      <div className={styles.playerContainer}>
        <EmbeddedVideoPlayer
          source={videoSourceId}
          embeddedVideoId={id}
          videoUrl={videoUrl}
          onEmbeddedVideoStarted={onVideoStart}
          onEmbeddedVideoEnded={onVideoEnd}
          onSkip={onSkip}
        />
      </div>
      <div className={styles.detailsWrapper}>
        <EmbeddedVideoDetails title={title} description={description} />
      </div>
    </div>
  );
};
